import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AlertCircle, ArrowLeft, ArrowRight } from 'lucide-react';

import { AuthLayout } from '../../components/auth/AuthLayout';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { invitationReducer } from '../../store/reducers/invitation.reducer';
import {
  getAcceptError,
  getAcceptSuccess,
  getInvitation,
  getInvitationError,
  getInvitationLoading,
  getWizardData,
} from '../../store/selectors/invitation.selector';
import { history } from '../../store/store';
import { Step1PersonalInfo } from './components/Step1PersonalInfo';
import { Step2WorkInfo } from './components/Step2WorkInfo';
import { Step3UseCase } from './components/Step3UseCase';
import { Step4ThankYou } from './components/Step4ThankYou';

const {
  getInvitation: getInvitationAction,
  updateWizardData,
  acceptInvitation,
  resetInvitationState,
} = invitationReducer.actions;

const steps = [
  "Let's get to know each other",
  'Tell us about your work',
  'What will you use TAMAN for',
  'Thank you for your responses',
];

/* ── Reusable error screen ───────────────────────────────────────────────── */

function ErrorScreen({ title, subtitle, message }: { title: string; subtitle: string; message: string }) {
  return (
    <AuthLayout title={title} subtitle={subtitle}>
      <div className="flex-1 flex flex-col items-center justify-center gap-5">
        <AlertCircle size={44} className="text-[#FF8A00]" />

        <div className="w-full flex items-start gap-2.5 rounded-lg px-3.5 py-3 bg-[#FF8A00]/10 border border-[#FF8A00]/30">
          <AlertCircle size={16} className="text-[#FF8A00] shrink-0 mt-0.5" />
          <p className="text-white text-xs text-left leading-relaxed">{message}</p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => history.push('/login')}
        className="w-full py-2.5 bg-green01 text-black text-sm font-medium rounded-xl
          transition-all active:scale-95 hover:bg-green02 shrink-0 mt-4"
      >
        Go to Login
      </button>
    </AuthLayout>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */

export const AcceptInvitationPage = () => {
  const [searchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fadeIn, setFadeIn] = useState(true);

  const dispatch = useAppDispatch();
  const invitation = useAppSelector(getInvitation);
  const loading = useAppSelector(getInvitationLoading);
  const error = useAppSelector(getInvitationError);
  const wizardData = useAppSelector(getWizardData);
  const acceptSuccess = useAppSelector(getAcceptSuccess);
  const acceptError = useAppSelector(getAcceptError);

  const token = searchParams.get('token');

  useEffect(() => {
    if (token) dispatch(getInvitationAction({ token }));
    return () => {
      dispatch(resetInvitationState());
    };
  }, [dispatch, token]);

  const isInvitationExpired = useMemo(() => {
    if (!invitation?.expiresAt) return false;
    return new Date(invitation.expiresAt) < new Date();
  }, [invitation]);

  const handleInputChange = (field: string, value: string) => {
    dispatch(updateWizardData({ [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};
    switch (step) {
      case 0:
        if (!wizardData.fullName.trim()) newErrors.fullName = 'Full name is required';
        if (!wizardData.password) newErrors.password = 'Password is required';
        else if (wizardData.password.length < 8) newErrors.password = 'Password must be at least 8 characters long';
        if (!wizardData.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
        else if (wizardData.password !== wizardData.confirmPassword)
          newErrors.confirmPassword = 'Passwords do not match';
        break;
      case 1:
        if (!wizardData.companyName.trim()) newErrors.companyName = 'Company name is required';
        if (!wizardData.industry) newErrors.industry = 'Industry is required';
        if (!wizardData.department) newErrors.department = 'Department is required';
        if (!wizardData.role) newErrors.role = 'Role is required';
        break;
      case 2:
        if (!wizardData.useCase) newErrors.useCase = 'Please select what you will use TAMAN for';
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const transitionStep = (next: number) => {
    setFadeIn(false);
    setTimeout(() => {
      setActiveStep(next);
      setFadeIn(true);
    }, 200);
  };

  const handleNext = () => {
    if (validateStep(activeStep)) transitionStep(activeStep + 1);
  };
  const handleBack = () => transitionStep(activeStep - 1);

  const handleFinish = () => {
    if (!token) return;
    dispatch(
      acceptInvitation({
        token,
        fullName: wizardData.fullName,
        password: wizardData.password,
        companyName: wizardData.companyName,
        industry: wizardData.industry,
        department: wizardData.department,
        role: wizardData.role,
        useCase: wizardData.useCase,
      }),
    );
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Step1PersonalInfo
            wizardData={wizardData}
            invitation={invitation}
            errors={errors}
            showPassword={showPassword}
            showConfirmPassword={showConfirmPassword}
            onInputChange={handleInputChange}
            onTogglePassword={() => setShowPassword((v) => !v)}
            onToggleConfirmPassword={() => setShowConfirmPassword((v) => !v)}
          />
        );
      case 1:
        return <Step2WorkInfo wizardData={wizardData} errors={errors} onInputChange={handleInputChange} />;
      case 2:
        return <Step3UseCase wizardData={wizardData} errors={errors} onInputChange={handleInputChange} />;
      case 3:
        return <Step4ThankYou acceptSuccess={acceptSuccess} acceptError={acceptError} />;
      default:
        return null;
    }
  };

  /* ── 1. Loading ────────────────────────────────────────────────────── */
  if (loading && !invitation) {
    return (
      <AuthLayout title="Loading..." subtitle="Please wait while we load your invitation.">
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-green01/30 border-t-green01 rounded-full animate-spin" />
        </div>
      </AuthLayout>
    );
  }

  /* ── 2. No token in URL ────────────────────────────────────────────── */
  if (!token) {
    return (
      <ErrorScreen
        title="Invalid Invitation"
        subtitle="We couldn't find your invitation."
        message="No invitation token was provided. Please check your invitation link and try again."
      />
    );
  }

  /* ── 3. API error ──────────────────────────────────────────────────── */
  if (error && !loading) {
    return (
      <ErrorScreen
        title="Invalid Invitation"
        subtitle="We couldn't find your invitation."
        message={error || 'No invitation found. Please check your invitation link.'}
      />
    );
  }

  /* ── 4. Not found (done loading, no error, but no invitation) ──────── */
  if (!loading && !invitation) {
    return (
      <ErrorScreen
        title="Invalid Invitation"
        subtitle="We couldn't find your invitation."
        message="No invitation found. Please check your invitation link."
      />
    );
  }

  /* ── 5. Expired ────────────────────────────────────────────────────── */
  if (isInvitationExpired) {
    return (
      <ErrorScreen
        title="Invitation Expired"
        subtitle="This invitation is no longer valid."
        message="This invitation has expired and can no longer be used. Please contact your administrator for a new invitation."
      />
    );
  }

  /* ── 6. Wizard ─────────────────────────────────────────────────────── */
  return (
    <AuthLayout title={steps[activeStep]} cardClassName="md:h-[720px]">
      {/* Zone 1 — Custom stepper (pinned to top) */}
      <div className="w-full shrink-0 mb-0 md:mb-4 mt-2 md:mt-2 flex justify-center">
        <div className="flex items-center gap-6 bg-neutral-900 rounded-full px-3 py-1">
          {steps.map((_, index) => {
            if (index === activeStep) {
              // Active step — concentric glow rings
              return (
                <div
                  key={index}
                  className="w-20 h-20 rounded-full bg-lime-900/40 border border-lime-700/30
                    flex items-center justify-center transition-all duration-300"
                >
                  <div className="w-16 h-16 rounded-full bg-lime-800/50 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-lime-400 flex items-center justify-center">
                      <span className="text-black font-bold text-lg">{index + 1}</span>
                    </div>
                  </div>
                </div>
              );
            }

            if (index < activeStep) {
              // Completed step — solid lime circle
              return (
                <div
                  key={index}
                  className="w-14 h-14 rounded-full bg-lime-400 flex items-center justify-center
                    transition-all duration-300 shadow-[0_0_12px_rgba(179,236,73,0.2)]"
                >
                  <span className="text-black font-bold text-lg">{index + 1}</span>
                </div>
              );
            }

            // Future step — grey outline
            return (
              <div
                key={index}
                className="w-14 h-14 rounded-full border border-zinc-600 flex items-center justify-center
                  transition-all duration-300"
              >
                <span className="text-zinc-500 text-lg">{index + 1}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Zone 2 — Content (grows to fill middle, vertically centered) */}
      <div
        className={`flex-1 flex flex-col mt-10 md:mt-0 justify-start md:justify-center min-h-0 overflow-auto transition-opacity duration-200 ${
          fadeIn ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {renderStepContent(activeStep)}
      </div>

      {/* Zone 3 — Footer buttons (pinned to bottom) */}
      <div className="flex justify-between items-center shrink-0 pt-3 md:pt-4">
        <button
          type="button"
          onClick={handleBack}
          disabled={activeStep === 0}
          className="flex items-center gap-1.5 px-4 py-2 border border-white/20 rounded-lg
            text-greyP2 text-sm transition-all
            hover:border-white/40
            disabled:text-white/[0.15] disabled:border-white/[0.06] disabled:cursor-not-allowed"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        {activeStep === steps.length - 1 ? (
          <button
            type="button"
            onClick={handleFinish}
            disabled={loading || acceptSuccess}
            className="flex items-center gap-2 px-6 py-2.5 bg-green01 text-black font-medium
              rounded-xl transition-all active:scale-95
              hover:bg-green02
              disabled:bg-green01/30 disabled:text-black/40 disabled:cursor-not-allowed disabled:active:scale-100"
          >
            {loading && <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />}
            {loading ? 'Processing...' : 'Finish'}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleNext}
            className="flex items-center gap-1.5 px-6 py-2.5 bg-green01 text-black font-medium
              rounded-xl transition-all active:scale-95 hover:bg-green02"
          >
            Continue
            <ArrowRight size={16} />
          </button>
        )}
      </div>
    </AuthLayout>
  );
};
